import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBookingFlowStore, selectCanAdvance } from '../../store/bookingFlowStore';
import { useClientTenantStore } from '../../store/clientTenantStore';
import { useBackButton } from '../../hooks/useBackButton';
import { ProviderStep } from '../../components/client/booking/ProviderStep';
import { ServiceStep } from '../../components/client/booking/ServiceStep';
import { TimeStep } from '../../components/client/booking/TimeStep';
import { ConfirmationStep } from '../../components/client/booking/ConfirmationStep';
import { SuccessScreen } from '../../components/client/booking/SuccessScreen';
import type { CreateBookingRes } from '../../types/client';

export default function BookingFlowPage() {
  const navigate = useNavigate();
  const { step: stepParam } = useParams<{ step: string }>();
  const step = parseInt(stepParam ?? '0', 10);

  const providerCount = useClientTenantStore((s) => s.providerCount);
  const providers = useClientTenantStore((s) => s.providers);
  const flowState = useBookingFlowStore();
  const canAdvance = selectCanAdvance(flowState, step, providerCount);
  const canAdvanceRef = useRef(canAdvance);
  canAdvanceRef.current = canAdvance;
  const setProvider = useBookingFlowStore((s) => s.setProvider);
  const setProviderDisplayName = useBookingFlowStore((s) => s.setProviderDisplayName);

  // providerCount === 1 bo'lsa — providerId va displayName avtomatik set qilinadi.
  // Til o'zgarganda providers ro'yxati yangi tilda keladi — displayName ham
  // doimo eng so'nggi qiymatga sinxron bo'lishi shart (cached uz/ru aralashib
  // qolmasin).
  useEffect(() => {
    if (providerCount === 1 && providers.length === 1) {
      const only = providers[0];
      if (flowState.providerId === null) {
        setProvider(only.id, only.displayName);
      } else if (flowState.providerDisplayName !== only.displayName) {
        setProviderDisplayName(only.displayName);
      }
    }
  }, [providerCount, providers, flowState.providerId, flowState.providerDisplayName, setProvider, setProviderDisplayName]);

  const [successResult, setSuccessResult] = useState<CreateBookingRes | null>(null);
  const [conflictError, setConflictError] = useState(false);

  const steps = useMemo<string[]>(
    () =>
      providerCount > 1
        ? ['provider', 'service', 'time', 'confirm']
        : ['service', 'time', 'confirm'],
    [providerCount],
  );

  const totalSteps = steps.length;

  // canAdvanceRef orqali o'qish — goNext faqat step/totalSteps o'zgarganda qayta yaratiladi,
  // canAdvance o'zgarganda EMAS. Bu useMainButton'da keraksiz hide→show siklini oldini oladi.
  const goNext = useCallback(() => {
    if (!canAdvanceRef.current) return;
    const nextStep = step + 1;
    if (nextStep < totalSteps) {
      navigate(`/booking/new/${nextStep}`);
    }
  }, [step, totalSteps, navigate]); // canAdvance intentionally omitted — ref orqali o'qiladi

  const goBack = useCallback(() => {
    if (step > 0) {
      navigate(`/booking/new/${step - 1}`);
    } else {
      navigate('/');
    }
  }, [step, navigate]);

  useBackButton(goBack);

  const handleConflict = useCallback(() => {
    setConflictError(true);
    const timeStepIndex = steps.indexOf('time');
    if (timeStepIndex !== -1) {
      navigate(`/booking/new/${timeStepIndex}`);
    }
  }, [steps, navigate]);

  const handleSuccess = useCallback((res: CreateBookingRes) => {
    setSuccessResult(res);
  }, []);

  if (successResult !== null) {
    return <SuccessScreen result={successResult} />;
  }

  const currentStepName = steps[step] ?? 'service';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: '8px 16px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
      }}>
        <div style={{ flex: 1, height: 4, borderRadius: 2, backgroundColor: 'var(--bl-divider)', overflow: 'hidden' }}>
          <div style={{
            width: `${((step + 1) / totalSteps) * 100}%`,
            height: '100%',
            backgroundColor: 'var(--bl-primary)',
            borderRadius: 2,
            transition: 'width 0.2s ease',
          }} />
        </div>
        <span style={{ fontSize: 11, color: 'var(--bl-text-secondary)', flexShrink: 0 }}>
          {step + 1}/{totalSteps}
        </span>
      </div>
      <div style={{ flex: 1 }}>
        {currentStepName === 'provider' && <ProviderStep onNext={goNext} />}
        {currentStepName === 'service' && <ServiceStep onNext={goNext} />}
        {currentStepName === 'time' && (
          <TimeStep
            onNext={goNext}
            conflictError={conflictError}
            onConflictAck={() => setConflictError(false)}
          />
        )}
        {currentStepName === 'confirm' && (
          <ConfirmationStep onSuccess={handleSuccess} onConflict={handleConflict} />
        )}
      </div>
    </div>
  );
}
